import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useOfferList from 'hooks/Offers/useOfferList'
import OfferCarouselFilters from './common/OfferCarouselFilters'
import OfferCarouselHeader from './common/OfferCarouselHeader'
import { MAX_CAROUSEL_ITEM_COUNT } from './common/constants'
import { useAppDispatch } from 'hooks/reduxHooks'
import { fetchToursPopularDestinations } from 'actions/GlobalSearch/GlobalSearchToursActions'
import { ANYWHERE_PLACE_ID } from 'constants/search'
import { LOOKING_FOR_SOMETHING_ELSE_CONTENT } from '../LookingForSomethingElse/constants'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import { rem } from 'polished'
import styled from 'styled-components'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import { OFFER_TYPE_TOUR_V2 } from 'constants/offer'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import OfferCarouselLookingForSomethingElse from '../LookingForSomethingElse/OfferCarouselLookingForSomethingElse'
import useTourFacetLocationCategory, { TourDestination } from 'hooks/TourV2/useTourFacetLocationCategory'

const SectionContainer = styled(VerticalSpacer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

const OFFER_TYPES: App.OfferListOfferType[] = ['tour', 'tour_v2']
const HOLIDAY_TYPES = ['!Cruises']
const EVERYWHERE_DESTINATION: TourDestination = {
  id: ANYWHERE_PLACE_ID,
  name: 'Around the world',
}

const DEFAULT_FILTERS: App.OfferListFilters = {
  offerTypes: OFFER_TYPES,
  frontPageVisibleOnly: true,
  holidayTypes: HOLIDAY_TYPES,
  destinationId: EVERYWHERE_DESTINATION.name,
}

interface Props {
  title?: React.ReactNode;
}

function TourOfferCarousel(props: Props) {
  const { title } = props
  const [selectedDestination, setSelectedDestination] = useState<TourDestination>(EVERYWHERE_DESTINATION)
  const isManualSortingEnabled = isManualSortingEnabledFor('tours')

  const offerListFilters = useMemo((): App.OfferListFilters => {
    if (isManualSortingEnabled && selectedDestination === EVERYWHERE_DESTINATION) {
      return {
        offerTypes: [OFFER_TYPE_TOUR_V2],
        sortBy: 'vertical-tour',
        limit: MAX_CAROUSEL_ITEM_COUNT,
        destinationId: selectedDestination.id,
        destinationName: selectedDestination.name,
      }
    }

    return {
      ...DEFAULT_FILTERS,
      destinationId: selectedDestination.id,
      destinationName: selectedDestination.name,
    }
  }, [isManualSortingEnabled, selectedDestination])

  const offerList = useOfferList(offerListFilters)

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchToursPopularDestinations())
  }, [dispatch])

  const tourFacets = useTourFacetLocationCategory({})

  const filters: TourDestination[] = useMemo(() => {
    return [EVERYWHERE_DESTINATION, ...Array.from(tourFacets.locations.values())]
  }, [tourFacets])

  const filterOptionStrings = useMemo(() => filters.map(f => f.name), [filters])

  const onFilterClick = useCallback((name: string) => {
    if (selectedDestination.name !== name) {
      const newSelection = filters.find(f => f.name === name)
      if (newSelection) {
        setSelectedDestination(newSelection)
      }
    }
  }, [filters, selectedDestination.name])

  const to = useMemo(() => {
    if (offerListFilters.destinationId && offerListFilters.destinationId !== ANYWHERE_PLACE_ID) {
      const query = {
        destinationId: offerListFilters.destinationId,
        destinationName: offerListFilters.destinationName ?? '',
      }
      return `/search/tours?${new URLSearchParams(query)}`
    }
    return LOOKING_FOR_SOMETHING_ELSE_CONTENT.tours.to
  }, [offerListFilters])

  if ((offerList.offerIds.length === 0) && filters.length === 0) {
    return null
  }

  return (
    <SectionContainer gap={20}>
      <LayoutContainer>
        <VerticalSpacer gap={20}>
          <OfferCarouselHeader allTo="/tours">
            {title ?? <>Once-in-a-lifetime <i>tours</i></>}
          </OfferCarouselHeader>
          <OfferCarouselFilters
            options={filterOptionStrings}
            active={selectedDestination.name}
            onClick={onFilterClick}
          />
        </VerticalSpacer>
      </LayoutContainer>
      <OfferListCarousel
        filters={offerListFilters}
        mobileLimit={8}
      >
        <OfferCarouselLookingForSomethingElse to={to} />
      </OfferListCarousel>
    </SectionContainer>
  )
}

export default React.memo(TourOfferCarousel)
